import CountDown from "@/components/CountDown";
import HBPopover from "@/components/HBPopover";
import Image from "@/components/Image";
import { COMPANY_GOOD_CODE, getVipTypeById } from "@/constants/vip";
import { getGrocery } from "@/services";
import { gdImgSrcPolicy } from "@/utils/imageUrl";
import useRequest from "@/utils/useReq";
import { useCallback, useMemo, useState } from "react";
import HBIcon from "../../HBIcon";
import Pay from "../../Pay";
import styles from "./index.module.less";

/**
 * @typedef {(order: Object)=>void} fn
 * @param {{
 *  details?: Object[],
 *  order?: Object,
 *  scene: "normal"|"upgrade"|"addition",
 *  currentVipType: "company"|"personal",
 *  banner?: string,
 *  onOrderChange: fn,
 *  onOrderDone: fn,
 *  onOrderFaild: fn,
 *  onOrderPaid: fn,
 *  onRefresh: ()=>void,
 * } & import("react").HTMLAttributes} props
 * @returns
 */
export default function PayPage({
  details,
  order,
  banner,
  scene = "normal",
  isVip = false,
  currentVipType = "company",
  onOrderChange,
  onOrderDone,
  onOrderFaild,
  onOrderPaid,
  onRefresh,
  ...rest
} = {}) {
  const { data: topBanner } = useRequest(
    async () => {
      if (banner) return banner;
      else {
        const res = await getGrocery("vip-dialog-banner");
        if (res.banner?.length) {
          return (
            res.banner[0]?.url ||
            "https://st0.dancf.com/static/02/202203310740-bef8.png"
          );
        } else {
          return "https://st0.dancf.com/static/02/202203310740-bef8.png";
        }
      }
    },
    {
      refreshDeps: [banner],
    }
  );
  const payParams = useMemo(() => {
    const skus = details
      .filter((d) => d.skuId)
      .map((item) => {
        return {
          sku_id: item.skuId,
          quantity: item.number,
          is_upgrade: scene === "upgrade" && item.code === COMPANY_GOOD_CODE,
        };
      });

    return {
      skus,
      scene,
      ...(scene == "normal" ? { isVip } : {}),
    };
  }, [details, isVip, scene]);

  const vipType = useMemo(() => {
    for (let i = 0; i < details.length; i++) {
      const d = details[i];
      const type = getVipTypeById(d.good?.vip_id);
      if (type) {
        return type;
      }
    }

    return currentVipType;
  }, [currentVipType, details]);

  const [countdownTime, setCountdownTime] = useState(order?.expiration_time);

  const refreshWrap = useCallback(
    (data) => {
      if (data) {
        setCountdownTime(data?.expiration_time);
      } else {
        setCountdownTime(null);
      }

      onRefresh && onRefresh(data);
    },
    [onRefresh]
  );

  return (
    <div className={styles.payWrap} {...rest}>
      {topBanner ? (
        <Image
          className={styles.banner}
          {...gdImgSrcPolicy(topBanner, { displayWidth: 621 })}
          alt="banner"
        ></Image>
      ) : (
        <div className={styles.banner}></div>
      )}

      <div className={styles.details}>
        <h3>付款明细</h3>
        <div className={styles.list}>
          {details
            .filter((d) => !d.type)
            .map((item) => (
              <div className={styles.item} key={item.name}>
                <div className={styles.name}>
                  <span>{item.name}</span>
                  <span>¥{item.price}</span>
                </div>
                <div className={styles.desc}>
                  <span>
                    {item.years && <span>{item.years}年</span>} 到期时间{" "}
                    {item.expireTime}
                  </span>
                  {!item.years && <span>x{item.number}</span>}
                </div>
              </div>
            ))}
          {details
            .filter((d) => d.type)
            .map((item) => (
              <div className={styles.item} key={item.name}>
                <div className={styles.name}>
                  <span>{item.name}</span>
                  <span className={styles.discountPrice}>-¥{-item.price}</span>
                </div>
              </div>
            ))}

          <div className={styles.settlement}>
            <span>应付金额：</span>
            <span>¥</span>
            <span>
              {order
                ? order.amount
                : details
                    .reduce(
                      (pre, cur) =>
                        pre + (cur.activity?.price || cur.price) * cur.number,
                      0
                    )
                    .toFixed(2)}
            </span>
          </div>
        </div>
        <div className={styles.qrcode}>
          <Pay
            {...(order
              ? {
                  order_no: order.order_no,
                  user_id: order.user_id,
                }
              : {
                  skuParam: payParams,
                })}
            onDone={onOrderDone}
            onFaild={onOrderFaild}
            onOrderChange={onOrderChange}
            onPaid={onOrderPaid}
            onRefresh={refreshWrap}
            style={{ boxShadow: "none", marginBottom: 12 }}
            size={130}
            padding={0}
            data-gd-expose="pay_expose"
            data-data-order-type="继续支付"
          />
          <div>
            <HBIcon
              type="a-icon-wechatpay"
              style={{ color: "#59CC0D", paddingRight: 4, fontSize: "16px" }}
            />
            <HBIcon
              type="a-icon-alipay"
              style={{ color: "#06B4FD", paddingRight: 4, fontSize: "16px" }}
            />
            使用微信/支付宝扫码支付
          </div>
          {countdownTime && (
            <div className={styles.time}>
              请在{" "}
              {
                <CountDown
                  targetTime={countdownTime}
                  style={{ display: "inline" }}
                />
              }{" "}
              内完成支付哦
            </div>
          )}
          {/* {countdown > 0 && (
            <div className={styles.time}>请在 {countdownTime} 内完成支付哦</div>
          )} */}
        </div>
      </div>

      <div className={styles.bottom}>
        <div>
          {" "}
          支付即视为您同意
          <a
            className={styles.link}
            href="/about/disclaimer?activeKey=2"
            target="_blank"
            rel="noreferrer"
          >
            《花瓣素材会员服务协议》
          </a>
          和
          {vipType === "company" ? (
            <a
              className={styles.link}
              href="/about/disclaimer?activeKey=5"
              target="_blank"
              rel="noreferrer"
            >
              《花瓣企业会员授权许可协议》
            </a>
          ) : (
            <a
              className={styles.link}
              href="/about/disclaimer?activeKey=6"
              target="_blank"
              rel="noreferrer"
            >
              《花瓣个人会员授权许可协议》
            </a>
          )}
        </div>
        <div>
          <HBPopover
            title={null}
            placement="topRight"
            content={
              <div className={styles.noticeInfo}>
                <span>
                  ·
                  企业会员指我们将合法保护您的企业授权身份；不提供多账号协作功能
                </span>
              </div>
            }
          >
            <a>
              购买须知
              <HBIcon type="ic_info" style={{ paddingLeft: 4 }} />
            </a>
          </HBPopover>
        </div>
      </div>
    </div>
  );
}
